import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CardCarousel, CardGrid } from '@iheartjane/menu';
import {
  Button,
  Flex,
  Loading,
  Skeleton,
  Typography,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router';

import type { UseFetchPlacement } from '@jane/dm/internal';
import type { AlgoliaProduct } from '@jane/search/types';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import type { RootLoaderType } from '../../../data-access';
import { useGetCart } from '../../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../../hooks';
import type { MenuCategory } from '../../../types';
import { InfiniteScrollThreshold } from '../InfiniteScrollThreshold';
import { MenuProductCard } from '../ProductCard/MenuProductCard';
import {
  productCardMaxWidth,
  productCardMinWidth,
} from '../ProductCard/ProductCard';
import { ProductCardSkeleton } from '../ProductCard/ProductCardSkeleton';

export interface MenuProductsProps {
  category: MenuCategory | 'special';
  hideUnavailableMessage?: boolean;
  searchProps: UseFetchPlacement<AlgoliaProduct>;
  showProductCount?: boolean;
  visiblePriceIds?: PriceId[];
}

export const MenuProducts = ({
  category,
  hideUnavailableMessage = false,
  searchProps,
  showProductCount = true,
  visiblePriceIds,
}: MenuProductsProps) => {
  const { pathname } = useLocation();
  const { inView, ref } = useInView();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: cart } = useGetCart();

  const { defaultSpacing, getKioskSpacing } = useKioskSpacing({
    defaultPadSize: 48,
    defaultSize: 64,
  });

  const midGridAdRow = useFlag(FLAGS.c3MidGridAdRow);

  const {
    dynamicPlacement,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isSuccess,
    products,
    numHits: productCount,
  } = searchProps;

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView]);

  return (
    <>
      {showProductCount && (
        <Flex
          alignSelf="flex-start"
          pl={defaultSpacing}
          pb={getKioskSpacing(32, 24)}
        >
          {isLoading && (
            <Skeleton animate>
              <Skeleton.Bone width="180px" />
            </Skeleton>
          )}
          {!isLoading && productCount !== 0 && (
            <Typography as="h2" variant="header-bold">
              {pluralize({
                noun: pathname.includes('search')
                  ? 'Product result'
                  : 'Product',
                number: productCount,
              })}
            </Typography>
          )}
        </Flex>
      )}
      {isSuccess && productCount === 0 && !hideUnavailableMessage && (
        <Typography px={defaultSpacing} variant="body">
          Bummer! Looks like all products are out of stock or unavailable. Check
          back later for restocks.
        </Typography>
      )}
      <CardGrid
        minCardWidth={productCardMinWidth}
        mobileColumns="auto"
        gap={getKioskSpacing(16, 24)}
        mobileGap={getKioskSpacing(16, 24)}
        px={defaultSpacing}
        pb={defaultSpacing}
      >
        {products?.length > 0
          ? products.map((product) => (
              <MenuProductCard
                highlightDeal={
                  category === 'specials' || category === 'special'
                }
                cart={cart}
                store={store}
                key={product.objectId}
                product={product}
                visiblePriceIds={visiblePriceIds}
              />
            ))
          : null}

        {isLoading &&
          Array(15)
            .fill(null)
            .map((_, index) => <ProductCardSkeleton key={index} />)}

        {midGridAdRow && dynamicPlacement && (
          <CardGrid.Placement background="primary" py={24}>
            <CardCarousel
              name={dynamicPlacement.title || ''}
              mode="ideal-width"
              cardWidth={productCardMaxWidth}
              cardWidthMobile={productCardMinWidth}
              cardGap={24}
              cardGapMobile={16}
              arrowVariant="filled"
            >
              {dynamicPlacement.products.map((product) => (
                <CardCarousel.Card
                  key={`dyn-${product.objectId}`}
                  width="100%"
                  widthMobile="100%"
                >
                  <MenuProductCard
                    cart={cart}
                    store={store}
                    product={product}
                    visiblePriceIds={visiblePriceIds}
                    disableWeightFilter
                  />
                </CardCarousel.Card>
              ))}
            </CardCarousel>
          </CardGrid.Placement>
        )}

        <InfiniteScrollThreshold ref={ref} />
      </CardGrid>

      <Flex width="100%" justifyContent="center" p={48} position="relative">
        {/**
         * Intersection Observer does not always work on Android devices.
         * This button is a fallback for those devices, and will only render when
         * the Intersection Observer is not "in view".
         */}
        {!inView && !isLoading && hasNextPage && (
          <Button label="Load more" onClick={() => fetchNextPage()} />
        )}
        {isLoading && <Loading color="black" size="lg" />}
      </Flex>
    </>
  );
};
